import { useState } from "react";

export type TipoAprovacao = 'simples' | 'assinatura';
export type ModoAssinatura = 'hierarquia' | 'sem-hierarquia';

export interface UsuarioObra {
  id: string;
  nome: string;
  email?: string;
  funcao?: string;
  aprovador?: boolean;
}

export interface ReportDefaults {
  condicoesClimaticas: boolean;
  maoDeObra: boolean;
  equipamentos: boolean;
  atividades: boolean;
  ocorrencias: boolean;
  materiais: boolean;
  comentarios: boolean;
  fotos: boolean;
  videos: boolean;
  anexos: boolean;
}

export interface NovaObraData {
  nomeObra: string;
  numeroObra: string;
  responsaveisObra: string[];
  clienteObra: string;
  clientesResponsaveis: string[];
  dataInicio?: Date | string;
  previsaoTermino?: Date | string;
  statusObra: string;
  endereco: {
    rua: string;
    numero: string;
    complemento: string;
    cidade: string;
    estado: string;
    cep: string;
    referencia: string;
    observacao: string;
  };
  reportDefaults: ReportDefaults;
  aprovacao: {
    tipo: TipoAprovacao;
    modo?: ModoAssinatura;
    participantes: UsuarioObra[];
  };
}

export const useNovaObra = () => {
  const [nomeObra, setNomeObra] = useState("");
  const [numeroObra, setNumeroObra] = useState("");
  const [responsaveisObra, setResponsaveisObra] = useState<string[]>([]);
  const [clienteObra, setClienteObra] = useState("");
  const [clientesResponsaveis, setClientesResponsaveis] = useState<string[]>([]);
  const [dataInicio, setDataInicio] = useState<Date>();
  const [previsaoTermino, setPrevisaoTermino] = useState<Date>();
  const [statusObra, setStatusObra] = useState("");
  const [endereco, setEndereco] = useState({
    rua: "",
    numero: "",
    complemento: "",
    cidade: "",
    estado: "",
    cep: "",
    referencia: "",
    observacao: ""
  });
  const [reportDefaults, setReportDefaults] = useState<ReportDefaults>({
    condicoesClimaticas: true,
    maoDeObra: true,
    equipamentos: true,
    atividades: true,
    ocorrencias: true,
    materiais: true,
    comentarios: true,
    fotos: true,
    videos: true,
    anexos: true,
  });
  const [aprovacao, setAprovacao] = useState<{
    tipo: TipoAprovacao;
    modo?: ModoAssinatura;
    participantes: UsuarioObra[];
  }>({
    tipo: 'simples',
    modo: 'sem-hierarquia',
    participantes: []
  });
  const [validationError, setValidationError] = useState<string>("");

  const resetForm = () => {
    setNomeObra("");
    setNumeroObra("");
    setResponsaveisObra([]);
    setClienteObra("");
    setClientesResponsaveis([]);
    setDataInicio(undefined);
    setPrevisaoTermino(undefined);
    setStatusObra("");
    setEndereco({
      rua: "",
      numero: "",
      complemento: "",
      cidade: "",
      estado: "",
      cep: "",
      referencia: "",
      observacao: ""
    });
    setReportDefaults({
      condicoesClimaticas: true,
      maoDeObra: true,
      equipamentos: true,
      atividades: true,
      ocorrencias: true,
      materiais: true,
      comentarios: true,
      fotos: true,
      videos: true,
      anexos: true,
    });
    setAprovacao({
      tipo: 'simples',
      modo: 'sem-hierarquia',
      participantes: []
    });
    setValidationError("");
  };

  const validateForm = (): boolean => {
    const hasAtLeastOneSection = Object.values(reportDefaults).some(value => value);
    if (!hasAtLeastOneSection) {
      setValidationError("Selecione pelo menos uma seção para o padrão do relatório");
      return false;
    }
    
    // Validar usuários da obra (opcional para teste)
    if (aprovacao.participantes.length === 0) {
      console.warn("Nenhum usuário adicionado à obra - permitindo para teste");
      // Para teste, vamos permitir criar obra sem usuários
    }
    
    if (aprovacao.tipo === 'assinatura') {
      const hasAprovador = aprovacao.participantes.some(user => user.aprovador);
      if (!hasAprovador) {
        setValidationError("Para aprovação por assinatura, defina pelo menos um aprovador");
        return false;
      }
    }
    
    setValidationError("");
    return true;
  };

  const getFormData = (): NovaObraData => ({
    nomeObra,
    numeroObra,
    responsaveisObra,
    clienteObra,
    clientesResponsaveis,
    dataInicio: dataInicio instanceof Date ? dataInicio.toISOString() : dataInicio,
    previsaoTermino: previsaoTermino instanceof Date ? previsaoTermino.toISOString() : previsaoTermino,
    statusObra,
    endereco,
    reportDefaults,
    aprovacao
  });

  return {
    // Form values
    nomeObra,
    numeroObra,
    responsaveisObra,
    clienteObra,
    clientesResponsaveis,
    dataInicio,
    previsaoTermino,
    statusObra,
    endereco,
    reportDefaults,
    aprovacao,
    validationError,
    // Setters
    setNomeObra,
    setNumeroObra,
    setResponsaveisObra,
    setClienteObra,
    setClientesResponsaveis,
    setDataInicio,
    setPrevisaoTermino,
    setStatusObra,
    setEndereco,
    setReportDefaults,
    setAprovacao,
    // Actions
    resetForm,
    getFormData,
    validateForm
  };
};